/**
 * CFO & Advisory › Benchmarks: the client's KPIs against the firm's other clients, anonymously
 * (median and middle half only), for the same three final months, with the peer group said in words.
 */
import { Exact } from "@clientflow/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Empty, Loading, Section } from "@/components/page";
import { errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { TableScroll } from "@/components/table-scroll";

type Row = {
  key: string;
  label: string;
  value: { display: string; exact: string | null };
  peers: number;
  outliers: number;
  smallBase: number;
  negativeBase?: number;
  noInventory?: number;
  reference?: { display: string; source: string; year: number; url: string | null } | null;
  note: string | null;
  stats: { q1: string; median: string; q3: string; q1Display: string; medianDisplay: string; q3Display: string } | null;
  position: "top_quarter" | "upper_middle" | "lower_middle" | "bottom_quarter" | "above_median" | "below_median" | "at_median" | null;
};
type Scope = "industry_band" | "industry" | "all";
type Data = {
  scope: Scope;
  peerCount: number;
  industry: string | null;
  industryLabel: string | null;
  band: string | null;
  bandLabel: string | null;
  bandMonths: number | null;
  bandEstimated: boolean | null;
  industryBandPeers: number;
  industryPeers: number;
  allPeers: number;
  minPeers: number;
  months: 3 | 12;
  smallGroup: boolean;
  reliablePeers: number;
  periods: string[];
  through: string | null;
  rows: Row[];
  /** How this client's DSO and DPO are measured, in plain words. */
  basisNote: string;
};

const n = (x: string) => Number(Exact.parse(x).toFixed(6));
const monthName = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
const clients = (k: number) => `${k} other client${k === 1 ? "" : "s"}`;

/** The peer group in plain words: who, and how many. */
function peerGroup(d: Data): string {
  const size = d.bandLabel ? ` with annual revenue of ${d.bandLabel.replace(/^[A-Z]/, (c) => c.toLowerCase())}` : "";
  if (d.scope === "industry_band") return `${clients(d.peerCount)} in ${d.industryLabel}${size}`;
  if (d.scope === "industry") return `${clients(d.peerCount)} in ${d.industryLabel} (any size)`;
  return `${clients(d.peerCount)} of the firm (all industries and sizes)`;
}
const POSITION: Record<NonNullable<Row["position"]>, { label: string; tone: string }> = {
  top_quarter: { label: "Top quarter", tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200" },
  upper_middle: { label: "Better than median", tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300" },
  lower_middle: { label: "Worse than median", tone: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200" },
  bottom_quarter: { label: "Bottom quarter", tone: "bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200" },
  above_median: { label: "Above median", tone: "bg-secondary text-foreground" },
  below_median: { label: "Below median", tone: "bg-secondary text-foreground" },
  at_median: { label: "At median", tone: "bg-secondary text-foreground" },
};

/** The peers' middle half as a band, their median as a tick, and the client as a dot, on a scale that fits all three. */
function RangeBar({ r }: { r: Row }) {
  if (!r.stats) return null;
  const q1 = n(r.stats.q1);
  const md = n(r.stats.median);
  const q3 = n(r.stats.q3);
  const v = r.value.exact ? n(r.value.exact) : null;
  const lo0 = Math.min(q1, v ?? q1);
  const hi0 = Math.max(q3, v ?? q3);
  const pad = (hi0 - lo0 || Math.abs(md) || 1) * 0.25;
  const lo = lo0 - pad;
  const hi = hi0 + pad;
  const x = (val: number) => `${((val - lo) / (hi - lo)) * 100}%`;
  return (
    <div className="relative h-5 w-full min-w-[120px]" role="img" aria-label={`Middle half of peers ${r.stats.q1Display} to ${r.stats.q3Display}, median ${r.stats.medianDisplay}; this client ${r.value.display}`}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--viz-axis)]" />
      <div className="absolute top-1/2 h-2.5 -translate-y-1/2 rounded-sm bg-[var(--viz-band)] ring-1 ring-[var(--viz-1)]/30" style={{ left: x(q1), width: `calc(${x(q3)} - ${x(q1)})` }} />
      <div className="absolute top-1/2 h-4 w-0.5 -translate-y-1/2 bg-[var(--viz-ink)]" style={{ left: x(md) }} />
      {v !== null && <div className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-card bg-[var(--viz-1)] shadow" style={{ left: x(v) }} />}
    </div>
  );
}

export function Benchmarks({ clientId, onOpenSettings }: { clientId: string; onOpenSettings: () => void }) {
  const [scope, setScope] = useState<"" | Scope>("");
  const [months, setMonths] = useState<3 | 12>(3);
  const qs = new URLSearchParams({ ...(scope ? { scope } : {}), ...(months === 12 ? { months: "12" } : {}) }).toString();
  const { data, isLoading, error } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/cfo/benchmarks${qs ? `?${qs}` : ""}`], placeholderData: (p) => p });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const refs = data.rows.filter((r) => r.reference);
  const hasRef = refs.length > 0;
  const range = data.periods.length ? `${monthName(data.periods[0]!)} to ${monthName(data.periods[data.periods.length - 1]!)}` : "";
  const options: Array<[Scope, string, boolean]> = [
    ["industry_band", `Same industry and size (${data.industryBandPeers})`, !data.industry || !data.band],
    ["industry", `Same industry (${data.industryPeers})`, !data.industry],
    ["all", `All clients (${data.allPeers})`, false],
  ];
  return (
    <Section
      title="Benchmarks"
      description={
        data.through
          ? `${range}, final figures only, against ${peerGroup(data)} for the same months. Peers are never named; only their median and middle half are shown.`
          : `Benchmarks need final figures for ${months === 12 ? "twelve" : "three"} months in a row for this client.`
      }
      action={
        <div className="flex max-w-full flex-wrap gap-2">
        <div role="radiogroup" aria-label="Months compared" className="inline-flex rounded-md border border-input bg-background p-0.5">
          {([3, 12] as const).map((m) => (
            <button key={m} type="button" role="radio" aria-checked={months === m} onClick={() => setMonths(m)} className={cn("rounded px-2.5 py-1 text-xs font-medium", months === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {m === 3 ? "Last 3 months" : "Last 12 months"}
            </button>
          ))}
        </div>
        <div role="radiogroup" aria-label="Compare with" className="inline-flex max-w-full flex-wrap rounded-md border border-input bg-background p-0.5">
          {options.map(([k, l, off]) => (
            <button key={k} type="button" role="radio" aria-checked={data.scope === k} disabled={off} onClick={() => setScope(k)} className={cn("rounded px-2.5 py-1 text-xs font-medium disabled:opacity-50", data.scope === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {l}
            </button>
          ))}
        </div>
        </div>
      }
    >
      {!data.industry && (
        <p className="mb-3 text-sm text-muted-foreground">
          No industry set for this client.{" "}
          <button type="button" className="font-medium text-primary hover:underline" onClick={onOpenSettings}>
            Set it in Settings
          </button>{" "}
          to compare with clients in the same industry.
        </p>
      )}
      {data.through && data.bandLabel && (
        <p className="mb-3 text-sm text-muted-foreground">
          Revenue size: {data.bandLabel} a year, from {data.bandEstimated ? `${data.bandMonths} months of final figures, annualized (an estimate)` : "the last 12 months"}.
          {!scope && data.industry && data.scope === "industry" ? ` Fewer than ${data.minPeers} other clients share this industry and size, so every size in the industry is used.` : ""}
          {!scope && data.industry && data.scope === "all" ? ` Fewer than ${data.minPeers} other clients share this industry, so all clients are used.` : ""}
        </p>
      )}
      {data.smallGroup && <p className="mb-3 text-sm text-amber-700 dark:text-amber-300">A small group: {data.peerCount} clients. Read the comparison with care{data.rows.some((r) => r.reference) ? ", alongside the published reference" : ""}.</p>}
      {data.scope !== "all" && data.peerCount < data.minPeers && <p className="mb-3 text-sm text-amber-700 dark:text-amber-300">Fewer than {data.minPeers} other clients in this group have final figures for these months, so their figures aren't shown. Compare with all clients instead.</p>}
      {!data.rows.length ? (
        <Empty>No benchmarks yet.</Empty>
      ) : (
        <>
        {/* The key for the bars, above the table so the columns keep their room. */}
        <div className="mb-2 flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--viz-1)]" /> This client
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2.5 w-4 rounded-sm bg-[var(--viz-band)] ring-1 ring-[var(--viz-1)]/30" /> Middle half of peers
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-0.5 bg-[var(--viz-ink)]" /> Peer median
          </span>
        </div>
        <TableScroll label="Benchmarks against peers table">
          <table className={cn("w-full text-sm", hasRef ? "min-w-[780px]" : "min-w-[680px]")}>
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Measure</th>
                <th className="px-3 py-2 text-right font-medium">This client</th>
                <th className="px-3 py-2 text-right font-medium">Peer median</th>
                <th className="px-3 py-2 text-right font-medium">Middle half of peers</th>
                {hasRef && <th className="px-3 py-2 text-right font-medium">Published</th>}
                <th className="w-36 px-3 py-2 font-medium">
                  <span className="sr-only">Where the client sits</span>
                </th>
                <th className="py-2 pl-3 font-medium">Position</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.rows.map((r) => (
                <tr key={r.key}>
                  <td className="py-2 pr-3">
                    {r.label}
                    {r.outliers + r.smallBase + (r.negativeBase ?? 0) + (r.noInventory ?? 0) > 0 && (
                      <span className="block text-xs text-muted-foreground">
                        {[
                          r.outliers ? `${r.outliers} unusual value${r.outliers === 1 ? "" : "s"} left out` : "",
                          r.smallBase ? `${r.smallBase} left out: the figure divided by is under $1,000` : "",
                          r.negativeBase ? `${r.negativeBase} left out: the figure divided by is negative` : "",
                          r.noInventory ? `${r.noInventory} left out: no inventory` : "",
                        ]
                          .filter(Boolean)
                          .join("; ")}
                      </span>
                    )}
                  </td>
                  <td className="tabular px-3 py-2 text-right font-semibold">{r.value.display}</td>
                  <td className="tabular px-3 py-2 text-right">{r.stats?.medianDisplay ?? "—"}</td>
                  <td className="tabular whitespace-nowrap px-3 py-2 text-right text-muted-foreground">{r.stats ? `${r.stats.q1Display} – ${r.stats.q3Display}` : `${r.peers} of ${data.minPeers} needed`}</td>
                  {hasRef && (
                    <td className="tabular whitespace-nowrap px-3 py-2 text-right" title={r.reference ? `${r.reference.source}, ${r.reference.year}` : undefined}>
                      {r.reference ? (r.reference.url ? <a href={r.reference.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{r.reference.display}</a> : r.reference.display) : "—"}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <RangeBar r={r} />
                  </td>
                  <td className="py-2 pl-3">
                    {r.position ? <span className={cn("whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", POSITION[r.position].tone)}>{POSITION[r.position].label}</span> : r.note ? <span className="text-xs text-muted-foreground">{r.note}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <p className="mt-2 text-xs text-muted-foreground">{data.basisNote}</p>
        {hasRef && (
          <p className="mt-2 text-xs text-muted-foreground">
            Published: {[...new Set(refs.map((r) => `${r.reference!.source} (${r.reference!.year})`))].join("; ")}, as entered by the firm for {data.industryLabel}.
          </p>
        )}
        </>
      )}
    </Section>
  );
}
